import React, { useState, useEffect } from 'react'
import {
  Box,
  Paper,
  Typography,
  Grid,
  Card,
  CardContent,
  CardHeader,
  Button,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Divider,
  LinearProgress,
  IconButton,
  Tooltip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow
} from '@mui/material'
import {
  CheckCircle,
  Cancel,
  Warning,
  Info,
  Send,
  History,
  Assignment,
  ArrowBack,
  Visibility,
  Edit,
  Person,
  CalendarToday
} from '@mui/icons-material'
import { useParams, useNavigate } from 'react-router-dom'
import { useSnackbar } from 'notistack'
import { format } from 'date-fns'
import medicalCodingService from '../../../services/medicalCodingService'

const CodingVerification = () => {
  const { codingId } = useParams()
  const navigate = useNavigate()
  const { enqueueSnackbar } = useSnackbar()
  
  const [loading, setLoading] = useState(false)
  const [coding, setCoding] = useState(null)
  const [verificationStatus, setVerificationStatus] = useState('')
  const [remarks, setRemarks] = useState('')
  const [confirmDialogOpen, setConfirmDialogOpen] = useState(false)
  const [selectedAction, setSelectedAction] = useState('')

  useEffect(() => {
    if (codingId) {
      loadCodingData()
    }
  }, [codingId])

  const loadCodingData = async () => {
    setLoading(true)
    try {
      const response = await medicalCodingService.getCodingById(codingId)
      
      if (response.data.success) {
        setCoding(response.data.data)
        setVerificationStatus(response.data.data.verificationStatus || 'PENDING')
      }
    } catch (error) {
      enqueueSnackbar('Failed to load coding data', { variant: 'error' })
    } finally {
      setLoading(false)
    }
  }

  const handleVerificationAction = (action) => {
    setSelectedAction(action)
    setConfirmDialogOpen(true)
  }

  const handleVerification = async () => {
    if (!selectedAction) {
      enqueueSnackbar('Please select a verification action', { variant: 'error' })
      return
    }

    if (selectedAction === 'QUERY_RAISED' && !remarks.trim()) {
      enqueueSnackbar('Remarks are required when raising a query', { variant: 'error' })
      return
    }

    setLoading(true)
    try {
      const response = await medicalCodingService.verifyCoding(codingId, {
        status: selectedAction,
        remarks: remarks.trim()
      })
      
      if (response.data.success) {
        enqueueSnackbar(`Coding ${selectedAction.toLowerCase().replace('_', ' ')} successfully`, { variant: 'success' })
        setConfirmDialogOpen(false)
        loadCodingData() // Reload to get updated data
      }
    } catch (error) {
      enqueueSnackbar('Failed to update verification status', { variant: 'error' })
    } finally {
      setLoading(false)
    }
  }

  const getStatusColor = (status) => {
    switch (status) {
      case 'VERIFIED': return 'success'
      case 'REJECTED': return 'error'
      case 'QUERY_RAISED': return 'warning'
      case 'PENDING': return 'default'
      default: return 'default'
    }
  }

  const getStatusIcon = (status) => {
    switch (status) {
      case 'VERIFIED': return <CheckCircle />
      case 'REJECTED': return <Cancel />
      case 'QUERY_RAISED': return <Warning />
      case 'PENDING': return <Info />
      default: return <Info />
    }
  }

  if (loading && !coding) {
    return (
      <Box sx={{ p: 3 }}>
        <LinearProgress />
        <Typography variant="body2" sx={{ mt: 2 }}>Loading coding data...</Typography>
      </Box>
    )
  }

  if (!coding) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error">Coding record not found</Alert>
      </Box>
    )
  }

  return (
    <Box sx={{ p: 3 }}>
      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
        <IconButton onClick={() => navigate(-1)} sx={{ mr: 2 }}>
          <ArrowBack />
        </IconButton>
        <Box sx={{ flex: 1 }}>
          <Typography variant="h5" fontWeight={600}>
            Medical Coding Verification
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {coding.mrdFile?.fileNumber} - {coding.mrdFile?.patientName}
          </Typography>
        </Box>
        <Chip
          icon={getStatusIcon(coding.verificationStatus)}
          label={coding.verificationStatus?.replace('_', ' ') || 'PENDING'}
          color={getStatusColor(coding.verificationStatus)}
          variant="outlined"
        />
      </Box>

      {loading && <LinearProgress sx={{ mb: 2 }} />}

      <Grid container spacing={3}>
        {/* Coding Details */}
        <Grid item xs={12} md={8}>
          {/* Primary Diagnosis */}
          <Card sx={{ mb: 3 }}>
            <CardHeader title="Primary Diagnosis" />
            <CardContent>
              <Grid container spacing={2}>
                <Grid item xs={12} md={3}>
                  <Typography variant="subtitle2" color="text.secondary">ICD-10 Code</Typography>
                  <Typography variant="body1" fontWeight={600}>
                    {coding.primaryDiagnosis?.icd10Code || 'N/A'}
                  </Typography>
                </Grid>
                <Grid item xs={12} md={6}>
                  <Typography variant="subtitle2" color="text.secondary">Description</Typography>
                  <Typography variant="body1">
                    {coding.primaryDiagnosis?.icd10Description || 'N/A'}
                  </Typography>
                </Grid>
                <Grid item xs={12} md={3}>
                  <Typography variant="subtitle2" color="text.secondary">Clinical Diagnosis</Typography>
                  <Typography variant="body1">
                    {coding.primaryDiagnosis?.diagnosis || 'N/A'}
                  </Typography>
                </Grid>
              </Grid>
            </CardContent>
          </Card>

          {/* Secondary Diagnoses */}
          {coding.secondaryDiagnoses && coding.secondaryDiagnoses.length > 0 && (
            <Card sx={{ mb: 3 }}>
              <CardHeader title={`Secondary Diagnoses (${coding.secondaryDiagnoses.length})`} />
              <CardContent>
                <TableContainer>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>Seq</TableCell>
                        <TableCell>ICD-10 Code</TableCell>
                        <TableCell>Description</TableCell>
                        <TableCell>POA</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {coding.secondaryDiagnoses.map((diagnosis, index) => (
                        <TableRow key={index}>
                          <TableCell>{diagnosis.sequence || index + 1}</TableCell>
                          <TableCell>{diagnosis.icd10Code}</TableCell>
                          <TableCell>{diagnosis.icd10Description}</TableCell>
                          <TableCell>
                            <Chip
                              label={diagnosis.presentOnAdmission ? 'Yes' : 'No'}
                              size="small"
                              color={diagnosis.presentOnAdmission ? 'success' : 'default'}
                            />
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </CardContent>
            </Card>
          )}

          {/* Procedures */}
          {coding.procedures && coding.procedures.length > 0 && (
            <Card sx={{ mb: 3 }}>
              <CardHeader title={`Procedures (${coding.procedures.length})`} />
              <CardContent>
                <TableContainer>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>Seq</TableCell>
                        <TableCell>ICD-10-PCS Code</TableCell>
                        <TableCell>Description</TableCell>
                        <TableCell>Date</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {coding.procedures.map((procedure, index) => (
                        <TableRow key={index}>
                          <TableCell>{procedure.sequence || index + 1}</TableCell>
                          <TableCell>{procedure.icd10pcsCode}</TableCell>
                          <TableCell>{procedure.description}</TableCell>
                          <TableCell>
                            {procedure.procedureDate ? format(new Date(procedure.procedureDate), 'MMM dd, yyyy') : 'N/A'}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </CardContent>
            </Card>
          )}

          {/* DRG Information */}
          {coding.drgGroup && (
            <Card sx={{ mb: 3 }}>
              <CardHeader title="DRG Information" />
              <CardContent>
                <Grid container spacing={2}>
                  <Grid item xs={12} md={3}>
                    <Typography variant="subtitle2" color="text.secondary">DRG Code</Typography>
                    <Typography variant="body1" fontWeight={600}>
                      {coding.drgGroup.code}
                    </Typography>
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <Typography variant="subtitle2" color="text.secondary">Description</Typography>
                    <Typography variant="body1">
                      {coding.drgGroup.description}
                    </Typography>
                  </Grid>
                  <Grid item xs={12} md={3}>
                    <Typography variant="subtitle2" color="text.secondary">Weight</Typography>
                    <Typography variant="body1" fontWeight={600}>
                      {coding.drgGroup.weight}
                    </Typography>
                  </Grid>
                </Grid>
              </CardContent>
            </Card>
          )}
        </Grid>

        {/* Verification Panel */}
        <Grid item xs={12} md={4}>
          <Card>
            <CardHeader 
              title="Verification Actions"
              action={
                <Tooltip title="View History">
                  <IconButton onClick={() => navigate(`/hospital/mrd/medical-coding/${codingId}/history`)}>
                    <History />
                  </IconButton>
                </Tooltip>
              }
            />
            <CardContent>
              {/* Current Status */}
              <Box sx={{ mb: 3 }}>
                <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                  Current Status
                </Typography>
                <Chip
                  icon={getStatusIcon(coding.verificationStatus)}
                  label={coding.verificationStatus?.replace('_', ' ') || 'PENDING'}
                  color={getStatusColor(coding.verificationStatus)}
                  size="medium"
                />
              </Box>

              {/* Coding Information */}
              <Box sx={{ mb: 3 }}>
                <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                  Coding Information
                </Typography>
                <List dense>
                  <ListItem>
                    <ListItemIcon><Person fontSize="small" /></ListItemIcon>
                    <ListItemText 
                      primary="Coded By"
                      secondary={coding.codedBy?.name || 'Unknown'}
                    />
                  </ListItem>
                  <ListItem>
                    <ListItemIcon><CalendarToday fontSize="small" /></ListItemIcon>
                    <ListItemText 
                      primary="Coded Date"
                      secondary={coding.codedAt ? format(new Date(coding.codedAt), 'MMM dd, yyyy HH:mm') : 'N/A'}
                    />
                  </ListItem>
                  {coding.verifiedBy && (
                    <ListItem>
                      <ListItemIcon><CheckCircle fontSize="small" /></ListItemIcon>
                      <ListItemText 
                        primary="Verified By"
                        secondary={coding.verifiedBy?.name || 'Unknown'}
                      />
                    </ListItem>
                  )}
                </List>
              </Box>

              <Divider sx={{ my: 2 }} />

              {/* Verification Actions */}
              {coding.verificationStatus !== 'VERIFIED' && (
                <Box>
                  <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                    Verification Actions
                  </Typography>
                  
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, mb: 2 }}>
                    <Button
                      variant="contained"
                      color="success"
                      startIcon={<CheckCircle />}
                      onClick={() => handleVerificationAction('VERIFIED')}
                      fullWidth
                    >
                      Verify & Approve
                    </Button>
                    
                    <Button
                      variant="outlined"
                      color="warning"
                      startIcon={<Warning />}
                      onClick={() => handleVerificationAction('QUERY_RAISED')}
                      fullWidth
                    >
                      Raise Query
                    </Button>
                    
                    <Button
                      variant="outlined"
                      color="error"
                      startIcon={<Cancel />}
                      onClick={() => handleVerificationAction('REJECTED')}
                      fullWidth
                    >
                      Reject
                    </Button>
                  </Box>
                </Box>
              )}

              {/* Previous Remarks */}
              {coding.verificationRemarks && (
                <Box sx={{ mt: 2 }}>
                  <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                    Previous Remarks
                  </Typography>
                  <Alert severity="info" sx={{ mt: 1 }}>
                    {coding.verificationRemarks}
                  </Alert>
                </Box>
              )}
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Confirmation Dialog */}
      <Dialog open={confirmDialogOpen} onClose={() => setConfirmDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>
          Confirm Verification Action
        </DialogTitle>
        <DialogContent>
          <Typography variant="body1" gutterBottom>
            Are you sure you want to {selectedAction?.toLowerCase().replace('_', ' ')} this medical coding?
          </Typography>
          
          <TextField
            fullWidth
            multiline
            rows={4}
            label="Remarks"
            value={remarks}
            onChange={(e) => setRemarks(e.target.value)}
            placeholder={
              selectedAction === 'QUERY_RAISED' 
                ? 'Please specify the query or concerns (required)'
                : 'Optional remarks or comments'
            }
            required={selectedAction === 'QUERY_RAISED'}
            sx={{ mt: 2 }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmDialogOpen(false)}>
            Cancel
          </Button>
          <Button 
            onClick={handleVerification}
            variant="contained"
            disabled={loading}
          >
            {loading ? 'Processing...' : 'Confirm'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  )
}

export default CodingVerification